import { Link, useLocation } from "wouter";
import {
  Sun, LayoutDashboard, MessageSquare, KanbanSquare, HeartPulse, Users, Calendar,
  Settings, ShieldCheck, LogOut, Menu, X, ChevronsUpDown,
} from "lucide-react";
import { cn } from "@client/lib/utils";
import { useLogout, useUser } from "@client/hooks/useAuth";
import { Avatar, AvatarFallback } from "@client/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@client/components/ui/dropdown-menu";
import { useUIStore } from "@client/store/uiStore";
import { Button } from "@client/components/ui/button";
import { Separator } from "@client/components/ui/separator";
import { Logo } from "@client/components/Logo";
import { useIsMobile } from "@client/hooks/useMediaQuery";

/** Order follows docs/07-information-architecture.md. */
const nav = [
  { href: "/app/hoje", icon: Sun, label: "Hoje" },
  { href: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/app/conversas", icon: MessageSquare, label: "Conversas" },
  { href: "/app/comercial", icon: KanbanSquare, label: "Comercial" },
  { href: "/app/acompanhamento", icon: HeartPulse, label: "Acompanhamento" },
  { href: "/app/pacientes", icon: Users, label: "Pacientes" },
  { href: "/app/agenda", icon: Calendar, label: "Agenda" },
];

export function Sidebar() {
  const [location] = useLocation();
  const logout = useLogout();
  const { sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNavOpen } = useUIStore();
  const isMobile = useIsMobile();

  const item = (href: string, Icon: typeof Sun, label: string) => {
    const active = location === href || location.startsWith(`${href}/`);
    return (
      <Link key={href} href={href}>
        <button
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 min-h-11 rounded-xl text-sm font-medium relative",
            "hover-elevate active-elevate-2 transition-colors",
            active
              ? "bg-[#47f6d8]/[0.12] text-[#47f6d8]"
              : "text-sidebar-foreground hover:text-white",
            sidebarCollapsed && "md:justify-center md:px-2",
          )}
          title={sidebarCollapsed ? label : undefined}
          aria-current={active ? "page" : undefined}
        >
          {active && (
            <span className="absolute -left-2 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[#47f6d8] shadow-[0_0_12px_rgba(71,246,216,0.6)]" />
          )}
          <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
          <span className={cn("truncate", sidebarCollapsed && "md:hidden")}>{label}</span>
        </button>
      </Link>
    );
  };

  return (
    <aside
      // Below `md` it is a drawer: full-width-ish, slid off-screen until asked for, and the collapse
      // width is ignored entirely — icon-only navigation makes no sense when it is an overlay.
      className={cn(
        // The public site's dark brand surface (styles/site.css): navy, a faint turquoise glow at the
        // top. The overlay tokens flip to light, as in the old dark theme, so hover still reads.
        "fixed left-0 top-0 bottom-0 z-40 flex flex-col overflow-hidden bg-sidebar text-sidebar-foreground",
        "[--elevate-1:rgb(255_255_255/0.06)] [--elevate-2:rgb(255_255_255/0.11)]",
        "w-72 transition-transform duration-(--duration-slow) ease-(--ease-out) md:transition-[width]",
        mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        sidebarCollapsed ? "md:w-16" : "md:w-60",
      )}
      inert={isMobile && !mobileNavOpen ? true : undefined}
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-[radial-gradient(80%_100%_at_30%_0%,rgba(71,246,216,0.14),transparent_70%)]" />
      <div className="relative h-16 flex items-center justify-between px-3 shrink-0">
        <span className="pl-1"><Logo size="md" variant="light" collapsed={sidebarCollapsed && !isMobile} /></span>
        <Button variant="ghost" size="icon" className="hidden shrink-0 text-sidebar-foreground hover:text-white md:inline-flex" onClick={toggleSidebar}
                aria-label={sidebarCollapsed ? "Expandir menu" : "Recolher menu"}>
          <Menu className="w-[18px] h-[18px]" strokeWidth={1.75} />
        </Button>
        <Button variant="ghost" size="icon" className="shrink-0 text-sidebar-foreground hover:text-white md:hidden" onClick={() => setMobileNavOpen(false)}
                aria-label="Fechar menu">
          <X className="w-[18px] h-[18px]" strokeWidth={1.75} />
        </Button>
      </div>

      <nav className="relative flex-1 py-3 px-3 space-y-1 overflow-y-auto">
        {nav.map((n) => item(n.href, n.icon, n.label))}
      </nav>

      <div className="relative px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-1 shrink-0">
        <Separator className="mb-2 bg-white/10" />
        {item("/app/configuracoes", Settings, "Configurações")}
        <SidebarAccount collapsed={sidebarCollapsed && !isMobile} onSignOut={() => logout.mutate()} />
      </div>
    </aside>
  );
}

function initials(name: string) {
  return name.trim().split(/\s+/).map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

/**
 * Who you are and which practice you are in, at the bottom of the sidebar, opening the account menu.
 * It lived in the desktop top bar until the bar was removed (design pass, 22/10); now it is in one
 * place on every viewport. The ring is the tenant's own colour (`--tenant`, useBrandTheme).
 */
function SidebarAccount({ collapsed, onSignOut }: { collapsed: boolean; onSignOut: () => void }) {
  const { data: user } = useUser();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" title={collapsed ? user.name : undefined}
                className={cn("mt-1 flex w-full items-center gap-3 rounded-xl p-2 text-left hover-elevate active-elevate-2",
                              collapsed && "md:justify-center")}>
          <Avatar className="h-9 w-9 shrink-0 select-none ring-2 ring-[color:var(--tenant,#47f6d8)] ring-offset-2 ring-offset-[#09141d]">
            <AvatarFallback className="bg-white/10 text-xs font-bold text-white">{initials(user.name)}</AvatarFallback>
          </Avatar>
          <span className={cn("min-w-0 flex-1 leading-tight", collapsed && "md:hidden")}>
            <span className="block truncate text-sm font-semibold text-white">{user.name}</span>
            <span className="block truncate text-xs text-sidebar-foreground">{user.tenant.name}</span>
          </span>
          <ChevronsUpDown className={cn("h-4 w-4 shrink-0 text-sidebar-foreground", collapsed && "md:hidden")} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60">
        <div className="px-2 py-1.5">
          <p className="truncate text-sm font-medium">{user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/app/configuracoes" className="flex w-full items-center gap-2">
            <Settings className="h-4 w-4" strokeWidth={1.75} /> Configurações
          </Link>
        </DropdownMenuItem>
        {/*
          The only link to the administrative area, and the only place it is named (ADR-057).
          Rendered behind `platformAdmin`, so a nutritionist's browser never receives it — the
          area answers 404 to her either way, and an item she could see but not open would
          announce exactly what the 404 is there to keep quiet.
        */}
        {user.platformAdmin && (
          <DropdownMenuItem asChild>
            <Link href="/admin" className="flex w-full items-center gap-2">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.75} /> Administração
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut} className="gap-2">
          <LogOut className="h-4 w-4" strokeWidth={1.75} /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
